/**
 * The rate-card form (POST / PATCH /pipeline-facility/rates).
 *
 * Currency is NEVER sent — a rate is in the company's currency, stamped by the
 * server (`currency_not_settable`). The fields that apply to one kind only are
 * shown — and sent — for that kind only: a crew role on a labour rate, a unit OR
 * an equipment type on an owned-equipment rate (`crew_role_not_labour`,
 * `equipment_not_equipment_kind`, `unit_and_type`).
 */
import React, { useState } from 'react';
import { blankToNull, toInput } from '../../../lib/pipeline-facility';
import type { PfCatalog } from '../../../types/pipeline-facility';
import type { PfRate, PfRateWrite } from '../../../types/pf-tickets';
import { PF_DATE_INPUT, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError } from '../PfBits';
import { PfFormGrid } from '../digs/DigBits';

export interface PfRateFormOptions {
  customers: { id: string; name: string }[];
  equipment: { id: string; name: string; unitNumber?: string | null }[];
  equipmentTypes: { id: string; name: string }[];
}

export function PfRateForm({
  rate,
  catalog,
  options,
  currency,
  today,
  defaultCustomerId,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  rate?: PfRate | null;
  catalog: PfCatalog | null;
  options: PfRateFormOptions;
  currency: string | null;
  today: string | null;
  defaultCustomerId?: string;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: PfRateWrite) => void;
}) {
  const [code, setCode] = useState(rate?.code ?? '');
  const [name, setName] = useState(rate?.name ?? '');
  const [kind, setKind] = useState(rate?.kind ?? '');
  const [unit, setUnit] = useState(rate?.unit ?? '');
  const [amount, setAmount] = useState(toInput(rate?.rate));
  const [customerId, setCustomerId] = useState(rate ? rate.customerId ?? '' : defaultCustomerId ?? '');
  const [crewRole, setCrewRole] = useState(rate?.crewRole ?? '');
  const [equipmentId, setEquipmentId] = useState(rate?.equipmentId ?? '');
  const [equipmentTypeId, setEquipmentTypeId] = useState(rate?.equipmentTypeId ?? '');
  const [from, setFrom] = useState(rate?.effectiveFrom ?? today ?? '');
  const [to, setTo] = useState(rate?.effectiveTo ?? '');
  const [description, setDescription] = useState(rate?.description ?? '');
  const [notes, setNotes] = useState(rate?.notes ?? '');
  const [problem, setProblem] = useState<string | null>(null);

  const submit = () => {
    setProblem(null);
    if (!code.trim()) return setProblem('Enter a short code for the rate.');
    if (!name.trim()) return setProblem('Enter the rate’s name.');
    if (!kind || !unit) return setProblem('Pick what kind of charge it is and its unit.');
    if (!amount.trim() || !Number.isFinite(Number(amount)) || Number(amount) < 0) return setProblem('Enter the rate as a number of 0 or more.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return setProblem('Enter the day the rate starts.');
    if (to && to < from) return setProblem('The end day can’t be before the start day.');
    if (equipmentId && equipmentTypeId) return setProblem('A rate prices one unit or one equipment type, not both. Pick one.');
    const body: PfRateWrite = {
      code: code.trim(),
      name: name.trim(),
      kind,
      unit,
      rate: amount.trim(),
      effective_from: from,
      effective_to: to || null,
      customer_id: customerId || null,
      description: blankToNull(description),
      notes: blankToNull(notes),
    };
    if (kind === 'labour') body.crew_role = crewRole || null;
    if (kind === 'equipment') {
      body.equipment_id = equipmentId || null;
      body.equipment_type_id = equipmentTypeId || null;
    }
    onSubmit(body);
  };

  return (
    <div data-testid="pf-rate-form" className="space-y-4 px-4 py-4">
      <PfFormError message={problem ?? error} />
      <PfFormGrid>
        <PfField label="Code *" hint="Short, e.g. LAB-OP. Unique per customer card and period.">
          <input className={PF_INPUT} maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} />
        </PfField>
        <PfField label="Name *">
          <input className={PF_INPUT} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        </PfField>
        <PfField label="Card" hint="The default card applies to every customer without a rate of their own.">
          <select className={PF_INPUT} aria-label="Card" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Default card (every customer)</option>
            {options.customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Kind *">
          <select className={PF_INPUT} aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Pick one</option>
            {(catalog?.priceKinds ?? []).map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Unit *">
          <select className={PF_INPUT} aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
            <option value="">Pick one</option>
            {(catalog?.priceUnits ?? []).map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label={`Rate (${currency ?? 'company currency'}) *`} hint="The company’s currency, set in Settings.">
          <input className={PF_INPUT} inputMode="decimal" aria-label="Rate" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </PfField>
        {kind === 'labour' && (
          <PfField label="Crew role" hint="Optional. Prices the hours of hands logged in this role.">
            <select className={PF_INPUT} aria-label="Crew role" value={crewRole} onChange={(e) => setCrewRole(e.target.value)}>
              <option value="">Any role</option>
              {(catalog?.crewRoles ?? []).map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </PfField>
        )}
        {kind === 'equipment' && (
          <>
            <PfField label="Equipment unit" hint="Optional. One unit, or one type — not both.">
              <select className={PF_INPUT} aria-label="Equipment unit" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
                <option value="">Any unit</option>
                {options.equipment.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.unitNumber ? `${q.unitNumber} · ${q.name}` : q.name}
                  </option>
                ))}
              </select>
            </PfField>
            <PfField label="Equipment type">
              <select className={PF_INPUT} aria-label="Equipment type" value={equipmentTypeId} onChange={(e) => setEquipmentTypeId(e.target.value)}>
                <option value="">Any type</option>
                {options.equipmentTypes.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </PfField>
          </>
        )}
        <PfField label="Starts *">
          <input className={PF_DATE_INPUT} type="date" aria-label="Starts" value={from} onChange={(e) => setFrom(e.target.value)} />
        </PfField>
        <PfField label="Ends" hint="Blank = no end day.">
          <input className={PF_DATE_INPUT} type="date" aria-label="Ends" value={to} onChange={(e) => setTo(e.target.value)} />
        </PfField>
      </PfFormGrid>
      <PfField label="Description" hint="Optional.">
        <input className={PF_INPUT} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
      </PfField>
      <PfField label="Notes">
        <textarea className={`${PF_INPUT} h-20 py-2`} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </PfField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={PF_PRIMARY} onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
